import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/hooks/useSettings";

const navItems = [
  { to: "/leaders", label: "Leaders", testid: "nav-home-link" },
  { to: "/editorials", label: "Editorials", testid: "nav-editorials-link" },
  { to: "/debates", label: "Debates", testid: "nav-debates-link" },
  { to: "/interviews", label: "Interviews", testid: "nav-interviews-link" },
  { to: "/polls", label: "Polls", testid: "nav-polls-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const MASTHEAD_H = 200; // approx. height that gets removed on collapse
    const onScroll = () => {
      const y = window.scrollY;
      const scrollableAfter =
        document.documentElement.scrollHeight - window.innerHeight - MASTHEAD_H;
      setCollapsed((prev) => {
        if (prev) return y > 60;
        // Only trigger collapse if there's genuinely room to stay collapsed.
        return scrollableAfter > 120 && y > 120;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="bg-neutral-200 sticky top-0 z-40">
      <div className="max-w-[1440px] mx-auto border-l border-r border-neutral-700 border-b border-black bg-white/95 backdrop-blur-sm">
      {/* Masthead */}
      <div
        data-testid="masthead-region"
        className={`relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden transition-all duration-300 ease-out ${
          collapsed
            ? "max-h-0 opacity-0 pt-0 pb-0 border-b-0"
            : "max-h-56 opacity-100 pt-6 pb-4 border-b border-neutral-200"
        }`}
      >
        {(settings.hero_bg_left_url || settings.hero_bg_right_url) && (
          // Always side-by-side (including on mobile) so the two halves stay at
          // the same level. Mobile uses `cover` (unchanged responsive cropping).
          // Desktop uses a height-constrained scale of 150% (images render 50%
          // bigger than the frame height; the frame stays the same and vertical
          // overflow is clipped by the masthead's overflow-hidden), anchored by
          // horizontal alignment (left → left edge, right → right edge).
          <div
            className="absolute inset-0 grid grid-cols-2 pointer-events-none"
            data-testid="masthead-split-bg"
            aria-hidden
          >
            <div
              className="bg-cover lg:!bg-[length:auto_150%] bg-no-repeat"
              style={settings.hero_bg_left_url ? {
                backgroundImage: `url(${settings.hero_bg_left_url})`,
                backgroundPosition: `${settings.hero_bg_left_align || "center"} center`,
              } : undefined}
            />
            <div
              className="bg-cover lg:!bg-[length:auto_150%] bg-no-repeat"
              style={settings.hero_bg_right_url ? {
                backgroundImage: `url(${settings.hero_bg_right_url})`,
                backgroundPosition: `${settings.hero_bg_right_align || "center"} center`,
              } : undefined}
            />
          </div>
        )}
        <div className="relative flex items-end justify-between gap-4">
          <Link to="/" data-testid="masthead-link" className="group flex items-center sm:items-end gap-4 sm:gap-5">
            <img
              src="/logo.png"
              alt="What Do We Want? When Do We Want It?"
              data-testid="masthead-logo"
              className="h-14 sm:h-20 w-auto shrink-0 select-none"
              draggable="false"
            />
            <div className="min-w-0">
              <span className="serif text-2xl sm:text-4xl lg:text-5xl font-medium leading-tight sm:leading-none tracking-tight block sm:whitespace-nowrap">
                What Do We Want? <br className="sm:hidden" /><span className="italic" style={{ color: "#fc4707" }}>When Do We Want It?</span>
              </span>
            </div>
          </Link>
          <div className="hidden md:block text-right mono text-[10px] uppercase tracking-[0.2em] text-black shrink-0 leading-relaxed">
            <div>{new Date().toLocaleDateString(undefined, { weekday: "long" })}</div>
            <div>{new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-12">
        <div className="flex items-center gap-6 overflow-x-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `mono text-xs uppercase tracking-[0.2em] whitespace-nowrap transition-colors hover:text-amber-700 ${
                  isActive ? "text-black border-b-2 border-black py-3" : "text-neutral-600 py-3"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {user && user !== false ? (
            <>
              {user.role === "admin" && (
                <NavLink
                  to="/admin"
                  data-testid="nav-admin-link"
                  className="mono text-xs uppercase tracking-[0.2em] text-black hover:text-[#fc4707] transition-colors"
                >
                  Editor&rsquo;s Desk
                </NavLink>
              )}
              <NavLink
                to="/submit"
                data-testid="nav-submit-link"
                style={{ color: "#fc4707", borderColor: "#fc4707" }}
                className="hidden sm:inline-flex bg-transparent border px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-[#fc4707] hover:text-white transition-colors"
              >
                Submit content
              </NavLink>
              <NavLink
                to="/account"
                data-testid="header-account-link"
                title="Account settings"
                className="hidden md:flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black transition-colors"
              >
                <span data-testid="header-user-name">{user.name}</span>
              </NavLink>
              <button
                onClick={handleLogout}
                data-testid="header-logout-button"
                className="mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black transition-colors"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <NavLink to="/login" data-testid="nav-login-link" className="border border-black px-4 py-2 mono text-xs uppercase tracking-[0.2em] text-neutral-700 hover:bg-black hover:text-white transition-colors">
                Sign in
              </NavLink>
              <NavLink to="/register" data-testid="nav-register-link" className="bg-black text-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 transition-colors">
                Join
              </NavLink>
            </>
          )}
        </div>
      </nav>
      </div>
    </header>
  );
}
